import React from 'react';
import { Grid, GridProps } from '../Grid';
import { countToColumns, widthToColumns } from './utils';

export interface SimpleGridProps extends GridProps {
  /**
   * The width at which child elements will break into columns. Pass a number for pixel values or a string for any other valid CSS length.
   */
  minChildWidth?: GridProps['minWidth'];
  /**
   * The number of columns
   */
  columns?: number | number[];
  /**
   * The gap between the grid items
   */
  spacing?: GridProps['gridGap'];
  /**
   * The column gap between the grid items
   */
  spacingX?: GridProps['gridGap'];
  /**
   * The row gap between the grid items
   */
  spacingY?: GridProps['gridGap'];
}

/**
 * Extends [Grid](../?path=/docs/general-layout-grid--docs)
 *
 * An alternative to the Grid component that aims to conveniently help with the average
 * use cases of a grid.
 *
 * Inspired from <a href="https://chakra-ui.com/" target="_blank">Chakra UI</a>
 */
export const SimpleGrid = React.forwardRef<HTMLDivElement, SimpleGridProps>(
  function SimpleGrid(props, ref) {
    const { columns, spacingX, spacingY, spacing, minChildWidth, ...rest } = props;
    let templateColumns;
    if (minChildWidth && (typeof minChildWidth === 'number' ? minChildWidth > 0 : true)) {
      templateColumns = widthToColumns(minChildWidth);
    } else if (columns && (typeof columns === 'number' ? columns > 0 : true)) {
      templateColumns = countToColumns(columns);
    }

    return (
      <Grid
        ref={ref}
        gap={spacing}
        columnGap={spacingX}
        rowGap={spacingY}
        templateColumns={templateColumns}
        {...rest}
      />
    );
  },
) as React.FC<SimpleGridProps>;
